/**
 * Spaced repetition (§11).
 *
 * A variant of SM-2, simplified for children: the grading scale is derived from
 * what we already record (was it right, how fast, how many hints) rather than
 * asking a nine-year-old to rate their own recall from 0 to 5.
 *
 * The intervals deliberately start where the design document does — 1, 2, 5,
 * 12, 30 days — and grow by the ease factor after that.
 */

import { DAY_MS, type ConceptState } from './mastery.ts';

export const INITIAL_INTERVALS = [1, 2, 5, 12, 30] as const;

export const MIN_EASE = 1.3;
export const MAX_EASE = 2.8;

/** How well the recall went, on SM-2's 0–5 scale. */
export type RecallQuality = 0 | 1 | 2 | 3 | 4 | 5;

export type AttemptSignal = {
  correct: boolean;
  /** Time to answer, in milliseconds. Optional — omit when not measured. */
  responseMs?: number;
  /** A hint or a second try means the memory was not retrieved cleanly. */
  usedHint?: boolean;
  attemptsOnItem?: number;
  /** 1–5, so an easy win on a hard item counts for more. */
  difficulty?: number;
};

/**
 * Turns an observed attempt into a recall grade.
 *
 * Speed matters, but only as a tie-break: a correct-but-slow answer is a 3
 * (recalled with effort), a correct-and-fast answer is a 5.
 */
export function qualityFromAttempt(signal: AttemptSignal): RecallQuality {
  if (!signal.correct) {
    return (signal.attemptsOnItem ?? 1) > 1 ? 1 : 0;
  }
  if (signal.usedHint || (signal.attemptsOnItem ?? 1) > 1) return 3;

  const seconds = signal.responseMs === undefined ? undefined : signal.responseMs / 1000;
  if (seconds === undefined) return 4;

  // Harder problems are allowed to take longer before they count as laboured.
  const budget = 8 + 6 * ((signal.difficulty ?? 2) - 1);
  if (seconds <= budget * 0.6) return 5;
  if (seconds <= budget) return 4;
  return 3;
}

export type ScheduleResult = {
  intervalDays: number;
  ease: number;
  dueAt: number;
  reviews: number;
};

/**
 * Schedules the next review.
 *
 * A failed recall resets the ladder — the concept comes back tomorrow — but
 * ease is only nudged down, so a single bad day does not permanently mark a
 * concept as hard.
 */
export function scheduleNext(state: ConceptState, quality: RecallQuality, now: number): ScheduleResult {
  const ease = clampEase(state.ease + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02)));

  if (quality < 3) {
    return { intervalDays: 1, ease, dueAt: now + DAY_MS, reviews: 0 };
  }

  const reviews = state.reviews + 1;
  const intervalDays =
    reviews <= INITIAL_INTERVALS.length
      ? (INITIAL_INTERVALS[reviews - 1] as number)
      : Math.round(state.intervalDays * ease);

  return { intervalDays, ease, dueAt: now + intervalDays * DAY_MS, reviews };
}

function clampEase(value: number): number {
  return Math.min(MAX_EASE, Math.max(MIN_EASE, Number(value.toFixed(3))));
}

export function isDue(state: ConceptState | undefined, now: number): boolean {
  if (!state || state.attempts === 0) return false;
  return state.dueAt !== undefined && state.dueAt <= now;
}

export function daysUntilDue(state: ConceptState | undefined, now: number): number | undefined {
  if (!state?.dueAt) return undefined;
  return (state.dueAt - now) / DAY_MS;
}

/**
 * Everything due now, most overdue first — the "warm up" queue on the student
 * home page. Concepts overdue by a long way come back before ones due today.
 */
export function dueConcepts(states: Readonly<Record<string, ConceptState>>, now: number): string[] {
  return Object.values(states)
    .filter((state) => isDue(state, now))
    .sort((a, b) => (a.dueAt ?? 0) - (b.dueAt ?? 0))
    .map((state) => state.conceptId);
}

/** A calendar of upcoming review load, for the progress page. */
export function reviewForecast(
  states: Readonly<Record<string, ConceptState>>,
  now: number,
  days = 14,
): { day: number; count: number }[] {
  const forecast = Array.from({ length: days }, (_, day) => ({ day, count: 0 }));
  for (const state of Object.values(states)) {
    if (state.dueAt === undefined) continue;
    const offset = Math.floor((state.dueAt - now) / DAY_MS);
    const index = Math.max(0, offset);
    if (index < days) (forecast[index] as { count: number }).count += 1;
  }
  return forecast;
}
